import { useEffect, useMemo, useState } from 'react';
import { GANTT_END_HOUR, GANTT_START_HOUR } from '../lib/mockData';
import { POOL_LABEL, type PoolId, type ResourceCardVM } from '../lib/planResources';
import { SectionBand } from './PlanShared';

const SPAN = GANTT_END_HOUR - GANTT_START_HOUR;
const HOURS = Array.from({ length: SPAN + 1 }, (_, i) => GANTT_START_HOUR + i);

function pct(hour: number): number {
  return ((hour - GANTT_START_HOUR) / SPAN) * 100;
}

interface DriverGanttProps {
  /** The active resource pool — same rows the strip is showing. */
  resources: ResourceCardVM[];
  activePool: PoolId | undefined;
  isLoading: boolean;
}

/**
 * Gantt — today's timeline for whichever resource pool the strip is showing
 * (drivers, trailers, vehicles or subcontractors), scoped to the selected depot.
 * One row per resource, an hour axis and a live "now" line.
 *
 * Assignment bars aren't shown yet: nothing links a job to a driver/vehicle in
 * the backend (a load carries a truck, not a driver), so every row reads "No
 * assignments" until that data exists.
 */
export function DriverGantt({ resources, activePool, isLoading }: DriverGanttProps) {
  // Tick every minute so the header time + now line track the clock.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const nowHour = now.getHours() + now.getMinutes() / 60;
  const nowWithinAxis = nowHour >= GANTT_START_HOUR && nowHour <= GANTT_END_HOUR;
  const headerLabel = useMemo(
    () =>
      `${now.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })} ▸ ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
    [now],
  );

  const label = activePool ? `Gantt — ${POOL_LABEL[activePool]} Timeline` : 'Gantt — Timeline';

  return (
    <SectionBand label={label} right={<span>{headerLabel}</span>} className="shrink-0">
      {/* Hour axis */}
      <div className="flex border-b border-slate-200 text-[10px] text-slate-400">
        <div className="w-40 shrink-0 border-r border-slate-200 px-1 py-1" />
        <div className="relative flex-1">
          <div className="flex">
            {HOURS.map((h) => (
              <div key={h} className="flex-1 px-1 py-1 text-center">
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rows — ~4 resources tall, then scroll vertically within the section. */}
      <div className="max-h-[196px] overflow-y-auto overflow-x-hidden">
        {isLoading && <div className="px-1 py-2 text-xs text-slate-400">Loading resources…</div>}
        {!isLoading && resources.length === 0 && (
          <div className="px-1 py-2 text-xs text-slate-400">Nothing available at this depot.</div>
        )}
        {resources.map((r) => (
          <div key={r.id} className="flex border-b border-slate-100 last:border-b-0">
            <div className="w-40 shrink-0 border-r border-slate-200 px-2 py-2">
              <div className="truncate text-xs font-medium text-slate-700">{r.title}</div>
              <div className="truncate text-[10px] text-slate-400">{r.subtitle}</div>
            </div>
            <div className="relative h-12 flex-1">
              {/* faint hour gridlines */}
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute bottom-0 top-0 w-px bg-slate-100"
                  style={{ left: `${pct(h)}%` }}
                />
              ))}
              {/* now line */}
              {nowWithinAxis && (
                <div
                  className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-red-400"
                  style={{ left: `${pct(nowHour)}%` }}
                />
              )}
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] italic text-slate-300">
                No assignments
              </span>
            </div>
          </div>
        ))}
      </div>
    </SectionBand>
  );
}
